import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiCamera, FiHeart, FiStar } from 'react-icons/fi';

const About = () => {
  const stats = [
    { number: '500+', label: 'Happy Clients' },
    { number: '50+', label: 'Weddings Captured' },
    { number: '10+', label: 'Years Experience' },
    { number: '1000+', label: 'Photos Delivered' }
  ];

  const testimonials = [
    {
      name: 'Sarah & Michael',
      type: 'Wedding Clients',
      text: 'Evan captured our special day perfectly. His attention to detail and artistic vision exceeded our expectations. The photos are absolutely stunning!',
      rating: 5
    },
    {
      name: 'Jennifer Martinez',
      type: 'Portrait Client',
      text: 'Professional, creative, and incredibly talented. Evan made me feel comfortable and confident during our session. The results were amazing!',
      rating: 5
    },
    {
      name: 'David Chen',
      type: 'Commercial Client',
      text: 'Evan\'s commercial photography helped elevate our brand. His work is consistently high-quality and he\'s a pleasure to work with.',
      rating: 5
    }
  ];

  const services = [
    {
      icon: <FiCamera className="h-8 w-8" />,
      title: 'Portrait Photography',
      description: 'Professional headshots, family portraits, and creative portraits that capture your unique personality and story.'
    },
    {
      icon: <FiHeart className="h-8 w-8" />,
      title: 'Wedding Photography',
      description: 'Comprehensive wedding coverage from engagement sessions to the big day, creating timeless memories.'
    },
    {
      icon: <FiStar className="h-8 w-8" />,
      title: 'Commercial Photography',
      description: 'Product photography, corporate events, and brand imagery that helps businesses stand out.'
    },
    {
      icon: <FiAward className="h-8 w-8" />,
      title: 'Event Photography',
      description: 'Birthday parties, corporate events, and special occasions captured with style and professionalism.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl font-serif font-bold text-gray-900 mb-6">
                About Evan Ventura
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                I'm a passionate photographer with over a decade of experience capturing life's most precious moments. 
                My journey began with a simple love for storytelling through images, and has evolved into a career 
                dedicated to creating timeless photographs that families treasure for generations.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Based in the heart of the city, I specialize in portrait photography, wedding coverage, and commercial work. 
                My approach combines technical expertise with artistic vision, ensuring every image tells a story and 
                captures the authentic essence of the moment.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80"
                alt="Evan Ventura"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-accent-500 text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">10+</div>
                <div className="text-sm">Years of Experience</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-accent-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center text-white"
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-lg opacity-90">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              What I Offer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive photography services tailored to your unique needs and vision
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <div className="text-accent-500 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-serif font-semibold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-8">
              My Philosophy
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              "Photography is more than just taking pictures—it's about capturing emotions, 
              preserving memories, and telling stories that will be cherished for generations. 
              Every client, every moment, every image is unique, and I approach each project 
              with the same passion and dedication."
            </p>
            <div className="text-lg text-accent-600 font-medium">
              - Evan Ventura
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">
              What Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take my word for it—here's what my clients have to say about their experience
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FiStar key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.type}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl font-serif font-bold mb-6"
          >
            Ready to Work Together?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl mb-8 opacity-90"
          >
            Let's discuss your vision and create something beautiful together
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href="/contact" className="btn-primary text-lg bg-white text-accent-600 hover:bg-gray-100">
              Get In Touch
            </a>
            <a href="/portfolio" className="btn-secondary text-lg border-white text-white hover:bg-white hover:text-accent-600">
              View Portfolio
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 